import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import GalleryEditor from "./GalleryEditor";
import { useToast } from "../hooks/use-toast";
import { Plus, Search, Trash2, Star, RefreshCw, Pencil, MapPin, Sparkles } from "lucide-react";

const ROOM_TYPES = ["Standard Room", "Deluxe Room", "Executive Suite", "Junior Suite", "Presidential Suite", "Family Room"];
const AMENITY_SUGGESTIONS = ["Free Wi-Fi", "Swimming Pool", "Spa", "Fitness Centre", "Restaurant", "Bar", "Airport Shuttle", "Free Parking", "Concierge", "Business Centre", "Pet Friendly", "Beach Access", "Room Service", "Laundry"];

const emptyForm = {
  name: "",
  brand: "AIP Signature",
  city: "",
  state: "",
  country: "India",
  address: "",
  phone: "",
  gstin: "",
  description: "",
  image: "",
  images: [],
  price_per_night: "",
  currency: "INR",
  star_rating: 4,
  room_types: ["Standard Room", "Deluxe Room"],
  room_packages: [],
  amenities: [],
  featured: false,
};

const BRANDS = ["AIP Collection", "AIP Blu", "AIP RED", "AIP Signature", "AIP Resorts", "AIP Inn"];

const HotelsManagement = () => {
  const { toast } = useToast();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customAmenity, setCustomAmenity] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listHotels(search ? { q: search } : {});
      setHotels(data);
    } catch (e) {
      toast({ title: "Failed to load hotels", description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRoomType = (rt) => {
    setForm((f) => {
      const nowHas = f.room_types.includes(rt);
      const room_types = nowHas ? f.room_types.filter((x) => x !== rt) : [...f.room_types, rt];
      // Sync packages: remove package when room type removed; add default package when added
      let room_packages = [...(f.room_packages || [])];
      if (nowHas) {
        room_packages = room_packages.filter((p) => p.room_type !== rt);
      } else if (!room_packages.find((p) => p.room_type === rt)) {
        room_packages.push({
          room_type: rt,
          price: parseFloat(f.price_per_night) || null,
          max_guests: rt.toLowerCase().includes("suite") ? 4 : 2,
          bed_type: rt.toLowerCase().includes("suite") ? "King" : "Queen",
          size_sqm: null,
          view: "",
          includes_breakfast: false,
          description: "",
          amenities: [],
        });
      }
      return { ...f, room_types, room_packages };
    });
  };
  const toggleAmenity = (a) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (h) => {
    setForm({
      name: h.name || "",
      brand: h.brand || "AIP Signature",
      city: h.city || "",
      state: h.state || "",
      country: h.country || "India",
      address: h.address || "",
      phone: h.phone || "",
      gstin: h.gstin || "",
      description: h.description || "",
      image: h.image || "",
      images: h.images || (h.image ? [h.image] : []),
      price_per_night: h.price_per_night || "",
      currency: h.currency || "INR",
      star_rating: h.star_rating || 4,
      room_types: h.room_types || [],
      room_packages: h.room_packages || [],
      amenities: h.amenities || [],
      featured: !!h.featured,
    });
    setEditingId(h.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.country || !form.price_per_night || form.room_types.length === 0) {
      toast({ title: "Missing fields", description: "Name, city, country, price and at least one room type are required." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price_per_night: parseFloat(form.price_per_night),
        star_rating: parseInt(form.star_rating) || 4,
        images: form.images || [],
        image: form.image || (form.images && form.images[0]) || "",
        room_packages: (form.room_packages || []).filter((p) => form.room_types.includes(p.room_type)),
      };
      if (editingId) {
        await api.updateHotel(editingId, payload);
        toast({ title: "Hotel updated", description: form.name });
      } else {
        const created = await api.createHotel(payload);
        toast({ title: "Hotel added", description: `${created.name} is now live on the site.` });
      }
      setOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e) {
      toast({ title: "Save failed", description: e.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete ${h.name}? Existing bookings will keep the hotel name.`)) return;
    try {
      await api.deleteHotel(h.id);
      toast({ title: "Hotel deleted" });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e.message });
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 border border-neutral-200 bg-white px-3 max-w-md">
          <Search size={16} className="text-neutral-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search hotels by name, city, brand..."
            className="flex-1 py-2 outline-none text-sm bg-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-none">
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={openNew} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none">
            <Plus size={16} className="mr-2" /> Add New Hotel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {hotels.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 text-neutral-500 border border-dashed border-neutral-300 bg-white">
            No hotels yet. Click "Add New Hotel" to list your first property.
          </div>
        )}
        {hotels.map((h) => (
          <div key={h.id} className="bg-white border border-neutral-200 group hover:border-[#c8102e] transition-colors">
            <div className="aspect-[16/10] bg-neutral-100 overflow-hidden relative">
              {h.image ? (
                <img src={h.image} alt={h.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No image</div>
              )}
              {h.featured && (
                <Badge className="absolute top-3 left-3 bg-[#c8102e] text-white rounded-none">
                  <Sparkles size={11} className="mr-1" /> Featured
                </Badge>
              )}
              <Badge className="absolute top-3 right-3 bg-black/70 text-white rounded-none">
                {h.brand}
              </Badge>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-lg font-medium leading-tight">{h.name}</h3>
                  <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><MapPin size={11} /> {h.city}, {h.country}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: h.star_rating || 0 }).map((_, i) => (
                    <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              {h.description && <p className="text-xs text-neutral-600 mt-3 line-clamp-2">{h.description}</p>}
              <div className="mt-3 flex flex-wrap gap-1">
                {(h.room_types || []).slice(0, 3).map((rt) => (
                  <span key={rt} className="text-[10px] uppercase tracking-widest border border-neutral-200 px-1.5 py-0.5 text-neutral-700">{rt}</span>
                ))}
                {(h.room_types || []).length > 3 && <span className="text-[10px] text-neutral-500">+{h.room_types.length - 3}</span>}
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">From</div>
                  <div className="text-lg font-serif font-medium">{h.currency} {Number(h.price_per_night).toLocaleString("en-IN")}<span className="text-xs text-neutral-500 font-sans font-normal"> / night</span></div>
                </div>
                <div className="flex items-center gap-1">
                  <button title="Edit" onClick={() => openEdit(h)} className="p-2 hover:bg-neutral-100 text-neutral-700 border border-neutral-200"><Pencil size={14} /></button>
                  <button title="Delete" onClick={() => remove(h)} className="p-2 hover:bg-rose-50 text-rose-700 border border-neutral-200 hover:border-rose-200"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingId ? "Edit Hotel" : "Add New Hotel"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <Label>Hotel name *</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. AIP Signature Rishikesh" />
            </div>
            <div>
              <Label>Brand *</Label>
              <Select value={form.brand} onValueChange={(v) => setField("brand", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Star rating</Label>
              <Select value={String(form.star_rating)} onValueChange={(v) => setField("star_rating", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{[3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} Star</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>City / Town *</Label>
              <Input value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder="e.g. Rishikesh" />
            </div>
            <div>
              <Label>State / Region</Label>
              <Input value={form.state} onChange={(e) => setField("state", e.target.value)} placeholder="e.g. Uttarakhand" />
            </div>
            <div className="md:col-span-2">
              <Label>Country *</Label>
              <Input value={form.country} onChange={(e) => setField("country", e.target.value)} placeholder="e.g. India" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="Street, area, PIN" />
            </div>
            <div>
              <Label>Hotel phone</Label>
              <Input data-testid="hotel-phone-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+91 22 1234 5678" />
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input data-testid="hotel-gstin-input" value={form.gstin} onChange={(e) => setField("gstin", e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" maxLength={15} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="A short, attractive overview visible on the public site." />
            </div>
            <div className="md:col-span-2">
              <Label>Hotel gallery — upload files or paste image URLs</Label>
              <GalleryEditor
                images={form.images}
                onChange={(imgs) => setField("images", imgs)}
                setPrimary={(url) => setField("image", url)}
                primary={form.image}
              />
            </div>
            <div>
              <Label>Price per night *</Label>
              <Input type="number" step="0.01" value={form.price_per_night} onChange={(e) => setField("price_per_night", e.target.value)} placeholder="7500" />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Booking types (room categories offered) *</Label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {ROOM_TYPES.map((rt) => (
                  <label key={rt} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${form.room_types.includes(rt) ? "border-[#c8102e] bg-[#c8102e]/5 text-[#c8102e]" : "border-neutral-200 hover:border-neutral-400"}`}>
                    <Checkbox checked={form.room_types.includes(rt)} onCheckedChange={() => toggleRoomType(rt)} />
                    {rt}
                  </label>
                ))}
              </div>
            </div>

            {/* Room packages editor */}
            {form.room_types.length > 0 && (
              <div className="md:col-span-2">
                <Label>Package details for each room type</Label>
                <p className="text-xs text-neutral-500 mt-1">Set price, occupancy, bed type, size and amenities so guests can see full details before booking.</p>
                <div className="mt-3 space-y-3">
                  {form.room_types.map((rt) => {
                    const pkg = (form.room_packages || []).find((p) => p.room_type === rt) || {
                      room_type: rt, price: form.price_per_night, max_guests: 2, bed_type: "Queen", size_sqm: null, view: "", includes_breakfast: false, description: "", amenities: [],
                    };
                    const update = (patch) => {
                      setForm((f) => {
                        const others = (f.room_packages || []).filter((p) => p.room_type !== rt);
                        return { ...f, room_packages: [...others, { ...pkg, ...patch }] };
                      });
                    };
                    return (
                      <div key={rt} className="border border-neutral-200 p-4 bg-neutral-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-medium text-sm">{rt}</div>
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <Checkbox checked={!!pkg.includes_breakfast} onCheckedChange={(v) => update({ includes_breakfast: !!v })} />
                            Breakfast included
                          </label>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Price / night</Label>
                            <Input type="number" step="0.01" value={pkg.price ?? ""} onChange={(e) => update({ price: e.target.value ? parseFloat(e.target.value) : null })} placeholder="7500" />
                          </div>
                          <div>
                            <Label className="text-xs">Max guests</Label>
                            <Input type="number" min="1" value={pkg.max_guests ?? 2} onChange={(e) => update({ max_guests: parseInt(e.target.value) || 1 })} />
                          </div>
                          <div>
                            <Label className="text-xs">Bed type</Label>
                            <Input value={pkg.bed_type || ""} onChange={(e) => update({ bed_type: e.target.value })} placeholder="King / Queen / Twin" />
                          </div>
                          <div>
                            <Label className="text-xs">Room size (sqm)</Label>
                            <Input type="number" step="0.1" value={pkg.size_sqm ?? ""} onChange={(e) => update({ size_sqm: e.target.value ? parseFloat(e.target.value) : null })} placeholder="32" />
                          </div>
                          <div className="col-span-2 md:col-span-2">
                            <Label className="text-xs">View</Label>
                            <Input value={pkg.view || ""} onChange={(e) => update({ view: e.target.value })} placeholder="Sea view / City view / Garden view" />
                          </div>
                          <div className="col-span-2 md:col-span-2">
                            <Label className="text-xs">Room amenities (comma separated)</Label>
                            <Input
                              value={(pkg.amenities || []).join(", ")}
                              onChange={(e) => update({ amenities: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                              placeholder="Balcony, Bathtub, Nespresso, Rain shower"
                            />
                          </div>
                          <div className="col-span-2 md:col-span-4">
                            <Label className="text-xs">Short description</Label>
                            <Textarea value={pkg.description || ""} onChange={(e) => update({ description: e.target.value })} placeholder="What makes this room special for guests?" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <Label>Amenities</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {AMENITY_SUGGESTIONS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => toggleAmenity(a)}
                    className={`text-xs px-3 py-1.5 border ${form.amenities.includes(a) ? "border-[#c8102e] bg-[#c8102e]/5 text-[#c8102e]" : "border-neutral-200 hover:border-neutral-400"}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Input value={customAmenity} onChange={(e) => setCustomAmenity(e.target.value)} placeholder="Add custom amenity" />
                <Button type="button" variant="outline" className="rounded-none" onClick={() => {
                  const v = customAmenity.trim();
                  if (v && !form.amenities.includes(v)) setField("amenities", [...form.amenities, v]);
                  setCustomAmenity("");
                }}>Add</Button>
              </div>
              {form.amenities.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {form.amenities.map((a) => (
                    <Badge key={a} variant="outline" className="rounded-none">
                      {a}
                      <button type="button" className="ml-2 text-neutral-500 hover:text-rose-600" onClick={() => toggleAmenity(a)}>×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex items-center gap-2 border border-neutral-200 bg-neutral-50 p-3">
              <Checkbox id="featured" checked={form.featured} onCheckedChange={(v) => setField("featured", !!v)} />
              <label htmlFor="featured" className="text-sm cursor-pointer">
                Feature this hotel on the home page hero row
              </label>
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-6">
                {submitting ? "Saving..." : editingId ? "Save changes" : "Publish hotel"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelsManagement;
